import React, { useEffect } from 'react';
import { useResumeContext } from '../../context/ResumeContext';

const FrameworkModule = () => {
  const { markSectionViewed } = useResumeContext();
  
  // Mark this section as viewed when component mounts
  useEffect(() => {
    markSectionViewed('framework');
  }, [markSectionViewed]);

  return (
    <div className="space-y-6">
      <section className="card border-l-4 border-purple-500">
        <h2 className="text-xl font-bold text-purple-700 mb-4">My Instructional Design Approach</h2>
        <p className="mb-6">
          My approach to creating effective instructional materials draws from both formal learning 
          principles and practical experience transforming complex technical information into 
          accessible content for diverse audiences.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-bold text-purple-800 flex items-center gap-2 mb-3">
              🎯 Analysis & Planning
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                <div>
                  <p className="font-medium">Audience Assessment</p>
                  <p className="text-sm text-slate-600">Identifying learner needs, technical background, and goals</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                <div>
                  <p className="font-medium">Content Mapping</p>
                  <p className="text-sm text-slate-600">Organizing information in logical learning sequences</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                <div>
                  <p className="font-medium">Learning Objectives</p>
                  <p className="text-sm text-slate-600">Defining clear, measurable outcomes for instruction</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-bold text-purple-800 flex items-center gap-2 mb-3">
              🔍 Design & Development
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                <div>
                  <p className="font-medium">Visual Frameworks</p>
                  <p className="text-sm text-slate-600">Creating diagrams and visual aids that clarify concepts</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">5</div>
                <div>
                  <p className="font-medium">Scaffolded Learning</p>
                  <p className="text-sm text-slate-600">Building knowledge progressively from simple to complex</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">6</div>
                <div>
                  <p className="font-medium">Interactive Components</p>
                  <p className="text-sm text-slate-600">Incorporating engagement elements that reinforce learning</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 bg-white border border-purple-200 rounded-lg p-4">
          <h3 className="font-bold text-purple-800 flex items-center gap-2 mb-3">
            ✅ Evaluation & Improvement
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">7</div>
              <div>
                <p className="font-medium">Feedback Integration</p>
                <p className="text-sm text-slate-600">Continuously revising based on user experiences and outcomes</p>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <div className="mt-1 bg-purple-200 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">8</div>
              <div>
                <p className="font-medium">Effectiveness Assessment</p>
                <p className="text-sm text-slate-600">Measuring learning impact through observable changes in behavior or understanding</p>
              </div>
            </li>
          </ul>
        </div>
      </section>
      
      <section className="card">
        <h2 className="text-xl font-bold mb-4">Learning Theory Applications</h2>
        <p className="mb-4">
          My work incorporates established learning principles, adapted for technical instruction:
        </p>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-3">
            <h3 className="font-bold text-slate-800 mb-1">Cognitive Load Management</h3>
            <p className="text-sm">Breaking complex processes into manageable chunks to prevent overwhelm</p>
          </div>
          <div className="border rounded-lg p-3">
            <h3 className="font-bold text-slate-800 mb-1">Visual Learning Enhancement</h3>
            <p className="text-sm">Using diagrams and visual aids to clarify abstract concepts</p>
          </div>
          <div className="border rounded-lg p-3">
            <h3 className="font-bold text-slate-800 mb-1">Active Learning Integration</h3>
            <p className="text-sm">Incorporating hands-on activities that reinforce conceptual understanding</p>
          </div>
          <div className="border rounded-lg p-3">
            <h3 className="font-bold text-slate-800 mb-1">Contextual Relevance</h3>
            <p className="text-sm">Connecting new information to practical applications and existing knowledge</p>
          </div>
          <div className="border rounded-lg p-3">
            <h3 className="font-bold text-slate-800 mb-1">Progressive Disclosure</h3>
            <p className="text-sm">Revealing information at the moment of need to prevent cognitive overload</p>
          </div>
          <div className="border rounded-lg p-3">
            <h3 className="font-bold text-slate-800 mb-1">Multimodal Presentation</h3>
            <p className="text-sm">Offering content in various formats to accommodate different learning preferences</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FrameworkModule;
